import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { eur, num, NA } from "@/lib/format";
import { InlineWarning } from "@/components/common/Primitives";

const TONE = {
  green: "border-emerald-300 bg-emerald-50 text-emerald-800",
  amber: "border-amber-300 bg-amber-50 text-amber-800",
  red: "border-red-300 bg-red-50 text-red-800",
};

export const ListPricePanel = ({ bpm, listingId, onChanged }) => {
  const lp = bpm?.list_price || null;
  const required = !!bpm?.requires_manual?.list_price;
  const [open, setOpen] = useState(required);
  const [form, setForm] = useState({ net_list_price: "", source_name: "", source_url: "", note: "" });
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  if (!lp && !required) return null;

  const run = async (label, fn) => {
    setBusy(label);
    setError("");
    try {
      const { data } = await fn();
      onChanged?.(data.analysis);
      setOpen(false);
    } catch (e) {
      setError(apiError(e));
    }
    setBusy("");
  };

  const save = () =>
    run("save", () =>
      api.post(`/listings/${listingId}/list-price`, {
        net_list_price: Number(form.net_list_price),
        source_name: form.source_name || "Handmatige invoer",
        source_url: form.source_url || null,
        note: form.note || null,
      })
    );

  const lookup = () => run("lookup", () => api.post(`/listings/${listingId}/list-price-lookup?force=true`));

  return (
    <div className="border border-slate-200" data-testid="bpm-list-price-panel">
      <div className="flex items-center justify-between border-b border-slate-200 bg-slate-100 px-3 py-1.5">
        <span className="label-xs">Netto-catalogusprijs (nodig voor tarieven t/m 2012)</span>
        {lp?.confidence !== undefined && lp?.confidence !== null && (
          <span className={`chip ${TONE[lp.confidence_tone] || TONE.amber}`}>
            {lp.confidence_label} · {num(lp.confidence)}%
          </span>
        )}
      </div>

      {required && (
        <div className="p-3">
          <InlineWarning tone="danger" testid="list-price-required">
            Geen Nederlandse catalogusprijs gevonden voor deze specificatie. Vul de netto-catalogusprijs
            in (of stel een definitieve BPM in) zodat landed cost, bijdrage, ROI en Deal Score kloppen.
          </InlineWarning>
        </div>
      )}

      {lp && (
        <div className="divide-y divide-slate-100">
          <Line label="Netto-catalogusprijs" value={eur(lp.net_list_price)} strong />
          <Line
            label="Bron"
            note={lp.match_criteria}
            value={lp.source_name || NA}
          />
          <Line
            label="Referentievoertuigen"
            note={(lp.examples || [])
              .slice(0, 4)
              .map((e) => `${e.reference} (${e.registration || "?"})`)
              .join(", ")}
            value={num(lp.reference_vehicle_count)}
          />
          {lp.gross_list_price ? (
            <Line
              label="Bruto consumentenprijs (RDW)"
              note={lp.vat_pct ? `btw ${num(lp.vat_pct)}%` : null}
              value={eur(lp.gross_list_price)}
            />
          ) : null}
          {lp.historic_gross_bpm ? (
            <Line label="Bruto BPM van NL-referentieauto's" value={eur(lp.historic_gross_bpm)} />
          ) : null}
          {lp.manual_override && (
            <Line label="Status" value="Handmatig ingevoerd (wordt nooit overschreven)" />
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2 border-t border-slate-200 bg-slate-50 px-3 py-2">
        <button
          type="button"
          data-testid="list-price-manual-toggle"
          onClick={() => setOpen((o) => !o)}
          className="rounded-sm border border-slate-300 bg-white px-2.5 py-1 text-[11.5px] font-semibold text-slate-700 hover:bg-slate-100"
        >
          {open ? "Sluiten" : "Catalogusprijs handmatig invullen"}
        </button>
        {listingId && (
          <button
            type="button"
            data-testid="list-price-lookup"
            onClick={lookup}
            disabled={busy === "lookup"}
            className="rounded-sm border border-slate-300 bg-white px-2.5 py-1 text-[11.5px] font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          >
            {busy === "lookup" ? "Zoeken bij RDW..." : "Opnieuw zoeken bij RDW"}
          </button>
        )}
      </div>

      {open && (
        <div className="space-y-2 border-t border-slate-200 p-3" data-testid="list-price-form">
          {error && <InlineWarning tone="danger">{error}</InlineWarning>}
          <div className="grid gap-2 md:grid-cols-2">
            <label className="block text-[11px] font-semibold text-slate-600">
              Netto-catalogusprijs (€)
              <input
                type="number"
                value={form.net_list_price}
                data-testid="list-price-input"
                onChange={(e) => setForm({ ...form, net_list_price: e.target.value })}
                className="mt-1 w-full rounded-sm border border-slate-300 px-2 py-1 text-[12px]"
              />
            </label>
            <label className="block text-[11px] font-semibold text-slate-600">
              Bron (verplicht)
              <input
                value={form.source_name}
                data-testid="list-price-source"
                onChange={(e) => setForm({ ...form, source_name: e.target.value })}
                placeholder="Bijv. originele NL prijslijst 2010"
                className="mt-1 w-full rounded-sm border border-slate-300 px-2 py-1 text-[12px]"
              />
            </label>
            <label className="block text-[11px] font-semibold text-slate-600">
              Bron-URL
              <input
                value={form.source_url}
                onChange={(e) => setForm({ ...form, source_url: e.target.value })}
                className="mt-1 w-full rounded-sm border border-slate-300 px-2 py-1 text-[12px]"
              />
            </label>
            <label className="block text-[11px] font-semibold text-slate-600">
              Notitie
              <input
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                className="mt-1 w-full rounded-sm border border-slate-300 px-2 py-1 text-[12px]"
              />
            </label>
          </div>
          <button
            type="button"
            onClick={save}
            data-testid="list-price-save"
            disabled={!form.net_list_price || busy === "save"}
            className="rounded-sm bg-blue-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {busy === "save" ? "Opslaan en herrekenen..." : "Opslaan en alles herrekenen"}
          </button>
          <p className="text-[10.5px] text-slate-500">
            Na opslaan worden BPM, landed cost, bijdrage, ROI en Deal Score direct opnieuw berekend.
            De waarde wordt bewaard voor alle technisch identieke auto's.
          </p>
        </div>
      )}
    </div>
  );
};

const Line = ({ label, value, note, strong }) => (
  <div className="flex items-start justify-between gap-3 px-3 py-1.5">
    <div className="min-w-0">
      <div className={`text-[12px] ${strong ? "font-semibold text-slate-900" : "text-slate-700"}`}>
        {label}
      </div>
      {note && <div className="truncate text-[10px] leading-tight text-slate-500">{note}</div>}
    </div>
    <div className={`mono shrink-0 text-[12px] ${strong ? "font-semibold text-slate-900" : "text-slate-800"}`}>
      {value}
    </div>
  </div>
);

export default ListPricePanel;
